import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Phone, Wallet, User, Loader2, Wifi, WifiOff, Trophy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TalkEngine } from "@/lib/talkRtc";
import CallOverlay from "./CallOverlay";
import GroupCallOverlay from "./GroupCallOverlay";

const Ctx = createContext(null);

// Opened from a call notification (/talk?call=ID): wait for the server to replay the ringing call, else explain
function CallLinkWatcher({ status, call, engine }) {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const want = sp.get("call");
  useEffect(() => {
    if (!want) return;
    if (call?.id === want) {
      if (sp.get("answer") === "1" && call.state === "incoming") {
        // only auto-answer when the mic is already allowed; otherwise the Accept button stays on screen
        navigator.permissions?.query({ name: "microphone" }).then((p) => p.state === "granted" && engine?.accept()).catch(() => {});
      }
      sp.delete("call"); sp.delete("answer"); setSp(sp, { replace: true }); return;
    }
    if (status !== "online") return;
    const t = setTimeout(() => { toast("That call has ended", { description: "You can call them back from Calls." }); nav("/talk/calls", { replace: true }); }, 6000);
    return () => clearTimeout(t);
  }, [want, call?.id, status]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
export const useTalk = () => useContext(Ctx);
export const tkErr = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong");

export function TkAvatar({ p, size = 44 }) {
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      {p?.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full rounded-2xl object-cover" />
        : <span className="w-full h-full rounded-2xl flex items-center justify-center font-black bg-gradient-to-br from-[#FF2BD6] to-[#00F0FF] text-black" style={{ fontSize: size * 0.42 }}>{(p?.display_name || "?")[0].toUpperCase()}</span>}
      {p?.online && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#39FF14] ring-2 ring-[#09090f]" data-testid="talk-online-dot" />}
    </span>
  );
}

export default function TalkLayout() {
  const { user, loading } = useAuth();
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [call, setCall] = useState(null);
  const [group, setGroup] = useState(null);
  const engineRef = useRef(null);
  const listeners = useRef(new Set());

  const reloadMe = useCallback(() => api.get("/talk/me").then((r) => setMe(r.data)).catch(() => {}), []);

  useEffect(() => { document.title = "Jixxeo Talk"; }, []);
  useEffect(() => {
    if (!user) return;
    reloadMe();
    const eng = new TalkEngine((type, ...args) => {
      if (type === "status") setStatus(args[0]);
      if (type === "call") setCall(args[0] ? { ...args[0] } : null);
      if (type === "error") toast.error(args[0]);
      if (type === "call_ended" && ["declined", "missed"].includes(args[0])) toast(args[0] === "declined" ? "Call declined" : "No answer");
      if (type === "message") reloadMe();
      if (type === "group") setGroup(args[0]);
      if (type === "gcall_ring") {
        const m = args[0];
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        toast(`${m.from.display_name} started a call in ${m.name}`, { duration: 30000, action: { label: "Join", onClick: () => eng.joinGroup({ id: m.chat_id, name: m.name }) } });
      }
      listeners.current.forEach((fn) => fn(type, ...args));
    });
    engineRef.current = eng;
    eng.connect();
    return () => eng.destroy();
  }, [user, reloadMe]);

  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const ctx = { me, reloadMe, engine: engineRef.current, subscribe, status, call, group };

  return (
    <div className="min-h-screen flex flex-col bg-[#09090f] text-[#f4f5ff]" style={{ fontFamily: "'Outfit',ui-sans-serif" }} data-testid="talk-layout">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#09090f]/85 border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/browse" className="text-xs mono uppercase tracking-widest text-white/75 hover:text-white py-2 pr-2" data-testid="talk-back">← Apps</Link>
          <div className="flex-1 font-heading font-black text-xl leading-none">
            Jixxeo <span className="text-[#FF2BD6]">Talk</span>
          </div>
          <span data-testid="talk-status" data-status={status}
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-full ${status === "online" ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-[#FF6A00]/15 text-[#FF6A00]"}`}>
            {status === "online" ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}{status === "online" ? "Online" : status === "connecting" ? "…" : "Offline"}
          </span>
        </div>
      </header>
      <main className="flex-1 pb-24">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {!user && !loading ? <LoginGate /> : !me ? <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF2BD6]" /></div>
            : <Ctx.Provider value={ctx}><Outlet /></Ctx.Provider>}
        </div>
      </main>
      {me && <Ctx.Provider value={ctx}><CallOverlay /><GroupCallOverlay /><CallLinkWatcher status={status} call={call} engine={engineRef.current} /></Ctx.Provider>}
      <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-[#09090f]/90 border-t border-white/10">
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          <Tab to="/talk" end icon={<span className="relative"><MessageCircle className="w-5 h-5" />{me?.unread > 0 && <span className="absolute -top-1.5 -right-2.5 bg-[#FF2BD6] text-black text-[9px] font-black rounded-full px-1" data-testid="talk-unread-badge">{me.unread}</span>}</span>} label="Chats" testId="talk-tab-chats" />
          <Tab to="/talk/calls" icon={<Phone className="w-5 h-5" />} label="Calls" testId="talk-tab-calls" />
          <Tab to="/talk/top" icon={<Trophy className="w-5 h-5" />} label="Top" testId="talk-tab-top" />
          <Tab to="/talk/wallet" icon={<Wallet className="w-5 h-5" />} label="Coins" testId="talk-tab-wallet" />
          <Tab to="/talk/me" icon={<User className="w-5 h-5" />} label="Me" testId="talk-tab-me" />
        </div>
      </nav>
    </div>
  );
}

function LoginGate() {
  return (
    <div className="py-16" data-testid="talk-login-gate">
      <MessageCircle className="w-14 h-14 text-[#FF2BD6]" />
      <h1 className="font-heading font-black text-4xl sm:text-5xl mt-4 leading-tight">Talk to anyone.<br /><span className="text-[#00F0FF]">Free.</span></h1>
      <p className="text-white/60 mt-3 text-sm max-w-sm">Private chats, photos, voice notes and HD voice & video calls with any Jixxeo member. Sign in once — it works in every Jixxeo app.</p>
      <div className="mt-7 flex flex-col sm:flex-row gap-3">
        <Link to="/register?next=/talk" data-testid="talk-signup-btn" className="bg-[#FF2BD6] hover:bg-[#ff5ce0] transition-colors text-black font-black px-6 py-3 rounded-full text-center">Create free account</Link>
        <Link to="/login?next=/talk" data-testid="talk-login-btn" className="border border-white/15 hover:bg-white/5 transition-colors font-black px-6 py-3 rounded-full text-center">Sign in</Link>
      </div>
    </div>
  );
}

function Tab({ to, icon, label, testId, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) => "flex flex-col items-center gap-0.5 py-2.5 text-[10px] uppercase tracking-widest transition-colors " + (isActive ? "text-[#FF2BD6]" : "text-white/45")}>
      {icon}<span className="font-bold">{label}</span>
    </NavLink>
  );
}
